import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { setLayersFilterAndModel, filterChanged } from '@/actions'
import { getLayerConfigUsingTocId } from '@/utilities/dataConfigSelectors'
import {
  BLANK_META_KEY,
  BLANK_FILTER_TYPES,
  convertFilterModelMamToSi,
} from '@/containers/DataTable/filterUtils'
import { toTimestamp } from '@/containers/DataTable/dateUtils'

const ApplyFilterToMap = ({
  doApplyFilter,
  currentFilter,
  gridApi,
  children,
}) => {
  const dispatch = useDispatch()
  const dataTable = useSelector(state => state.dataTable)
  const dataConfig = useSelector(state => state.updateDataConfig)

  const getExpressionType = type => {
    if (type === 'contains') {
      return 'in'
    }

    // TODO: FIND Solution for Not Contains.  Probably use index-of == -1
    if (type === 'notContains') {
      dispatch({
        type: 'ALERT',
        payload: {
          error: 'Not Contains Map Filter not yet supported, use Equals',
        },
      })
      return 'in'
    }

    if (type === 'equals') {
      return '=='
    }

    if (type === 'notEqual') {
      return '!='
    }
  }

  const getNumberFilterOperator = type => {
    switch (type) {
      case 'equals':
        return '=='
      case 'notEqual':
        return '!='
      case 'lessThan':
        return '<'
      case 'lessThanOrEqual':
        return '<='
      case 'greaterThan':
        return '>'
      case 'greaterThanOrEqual':
        return '>='
      default:
        return null
    }
  }

  const buildStringBlankExpression = prop => [
    '==',
    ['coalesce', ['get', prop], ''],
    '',
  ]

  const buildStringNotBlankExpression = prop => [
    '!=',
    ['coalesce', ['get', prop], ''],
    '',
  ]

  const buildNumberBlankExpression = prop => [
    'any',
    ['!', ['has', prop]],
    ['==', ['get', prop], null],
    ['==', ['get', prop], ''],
  ]

  const buildNumberNotBlankExpression = prop => [
    'all',
    ['has', prop],
    ['!=', ['get', prop], null],
    ['!=', ['get', prop], ''],
  ]

  // Every MapLibre style-layer id (fill + outline + symbol) backing a TOC layer.
  const collectStyleLayerIds = layersArray =>
    layersArray.flatMap(layerObj =>
      [layerObj.layer, layerObj.outline, layerObj.symbol]
        .filter(Boolean)
        .map(styleLayer => styleLayer.id)
    )

  useEffect(() => {
    // Add null check for dataTable before destructuring
    if (!dataTable) return

    const { tocId } = dataTable
    const tocObject = getLayerConfigUsingTocId(dataConfig, tocId)
    if (!tocObject) return
    const layersArray = tocObject.layersArray
    if (!layersArray) return

    const styleLayerIds = collectStyleLayerIds(layersArray)

    if (doApplyFilter) {
      // Note:  we save the filterModel to the layers metadata
      const filterModel = gridApi.getFilterModel()
      // Convert any MAM geometry field filter values from imperial → SI so
      // MapLibre expressions compare against the stored raw (SI) data correctly.
      const siCurrentFilter = convertFilterModelMamToSi(currentFilter) ?? {}
      /*
       NOTE: Expressions with the IN Operator take the following form
        ["in", FILTER_VALUE, ["get", FILTER_PROPERTY]]
        so filtering streetname prop that contains MIM would be
        ["in", "MIM", ["get","streetname"]]
        To search for many, wrap with any expression
       [
          "any",
          ["in", "MIM", ["get","streetname"]],
          ["in", "PEOR", ["get","streetname"]]
        ]
      */
      let expression = ['all']
      const filterKeys = Object.keys(siCurrentFilter)

      filterKeys.forEach(prop => {
        const filterEntry = siCurrentFilter[prop]
        if (!filterEntry) {
          return
        }

        if (filterEntry.filterType === 'set') {
          const containsExpression = []
          containsExpression.push('in')
          containsExpression.push(['get', prop])
          containsExpression.push(['literal'])
          containsExpression[2].push(filterEntry.values)
          expression.push(containsExpression)
          return
        }

        if (filterEntry.filterType === 'number') {
          const blankState = filterEntry[BLANK_META_KEY]
          if (blankState === BLANK_FILTER_TYPES.BLANK) {
            expression.push(buildNumberBlankExpression(prop))
            return
          }
          if (blankState === BLANK_FILTER_TYPES.NOT_BLANK) {
            expression.push(buildNumberNotBlankExpression(prop))
            return
          }

          const numFilter = filterEntry
          if (numFilter.operator) {
            const clause = numFilter.operator === 'OR' ? ['any'] : ['all']

            if (numFilter.condition1) {
              const condition1 = []
              const mapboxOp = getNumberFilterOperator(
                numFilter.condition1.type
              )
              if (mapboxOp) {
                condition1.push(mapboxOp)
                condition1.push(['get', prop])
                condition1.push(numFilter.condition1.filter)
                clause.push(condition1)
              }
            }

            if (numFilter.condition2) {
              const condition2 = []
              const mapboxOp = getNumberFilterOperator(
                numFilter.condition2.type
              )
              if (mapboxOp) {
                condition2.push(mapboxOp)
                condition2.push(['get', prop])
                condition2.push(numFilter.condition2.filter)
                clause.push(condition2)
              }
            }

            expression.push(clause)
          } else {
            const mapboxOp = getNumberFilterOperator(numFilter.type)
            if (mapboxOp) {
              const containsExpression = []
              containsExpression.push(mapboxOp)
              containsExpression.push(['get', prop])
              containsExpression.push(numFilter.filter)
              expression.push(containsExpression)
            }
          }
          return
        }

        if (filterEntry.filterType === 'date') {
          const dateFilter = filterEntry
          if (dateFilter.operator) {
            const clause = dateFilter.operator === 'OR' ? ['any'] : ['all']

            if (dateFilter.condition1) {
              const condition1 = []
              const mapboxOp = getNumberFilterOperator(
                dateFilter.condition1.type
              )
              if (mapboxOp) {
                condition1.push(mapboxOp)
                condition1.push(['to-number', ['get', prop]])
                condition1.push(toTimestamp(dateFilter.condition1.dateFrom))
                clause.push(condition1)
              }
            }

            if (dateFilter.condition2) {
              const condition2 = []
              const mapboxOp = getNumberFilterOperator(
                dateFilter.condition2.type
              )
              if (mapboxOp) {
                condition2.push(mapboxOp)
                condition2.push(['to-number', ['get', prop]])
                condition2.push(toTimestamp(dateFilter.condition2.dateFrom))
                clause.push(condition2)
              }
            }

            expression.push(clause)
          } else {
            const mapboxOp = getNumberFilterOperator(dateFilter.type)
            if (mapboxOp && dateFilter.dateFrom) {
              const containsExpression = []
              containsExpression.push(mapboxOp)
              containsExpression.push(['to-number', ['get', prop]])
              containsExpression.push(toTimestamp(dateFilter.dateFrom))
              expression.push(containsExpression)
            }
          }
          return
        }

        if (filterEntry.filterType === 'text') {
          const blankState = filterEntry[BLANK_META_KEY]
          if (blankState === BLANK_FILTER_TYPES.BLANK) {
            expression.push(buildStringBlankExpression(prop))
            return
          }
          if (blankState === BLANK_FILTER_TYPES.NOT_BLANK) {
            expression.push(buildStringNotBlankExpression(prop))
            return
          }

          if (filterEntry.operator) {
            const clause = filterEntry.operator === 'OR' ? ['any'] : ['all']
            const condition1 = []
            const condition2 = []
            if (filterEntry.condition1) {
              const type = getExpressionType(filterEntry.condition1.type)
              condition1.push(type)
              condition1.push(['upcase', filterEntry.condition1.filter])
              condition1.push(['upcase', ['get', prop]])
            }
            if (filterEntry.condition2) {
              const type = getExpressionType(filterEntry.condition2.type)
              condition2.push(type)
              condition2.push(['upcase', filterEntry.condition2.filter])
              condition2.push(['upcase', ['get', prop]])
            }
            if (condition1.length > 0) clause.push(condition1)
            if (condition2.length > 0) clause.push(condition2)
            if (clause.length > 1) expression.push(clause)
          } else {
            const containsExpression = []
            const type = getExpressionType(filterEntry.type)
            containsExpression.push(type)
            containsExpression.push(['upcase', filterEntry.filter])
            containsExpression.push(['upcase', ['get', prop]])
            expression.push(containsExpression)
          }
        }
      })
      if (expression.length > 1) {
        // Apply the expression + grid filterModel to all backing style layers.
        dispatch(
          setLayersFilterAndModel(styleLayerIds, expression, filterModel)
        )
      } else {
        // No usable expression — clear filter + stored filterModel.
        dispatch(setLayersFilterAndModel(styleLayerIds, null, null))
      }
    } else {
      // Filter toggled off — clear filter + stored filterModel.
      dispatch(setLayersFilterAndModel(styleLayerIds, null, null))
    }

    dispatch(filterChanged())
  }, [currentFilter, doApplyFilter])

  return children
}

export default ApplyFilterToMap
